/* eslint-disable react/jsx-props-no-spreading */
import * as ReactDOMClient from 'react-dom/client';
import { type FC } from 'react';
import HTMLReactParser from 'html-react-parser';
import type { FixedDeathCoverData, FixedDeathCalcProps } from '../types';
import Image from '2-core-elements/Image/Image';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useCalculatorQuery, useFixedDeathCalc, displayFormat } from '../calculatorLibs';
import { ListingError, ListingLoader } from '3-components/ListingApp/ListingUI/ListingUI';
import SimpleStatisticCard from '3-components/SimpleStatisticCard/SimpleStatisticCard';
import FixedDeathCalcForm from './FixedDeathCalcForm';

const FixedDeathCalc: FC<FixedDeathCalcProps> = ({ label, table, dataUrl }) => {
    const {
        titleText,
        subtitleText,
        infoText,
        labelAge,
        labelCover,
        labelGender,
        imageDesktopUrl,
        calculateText,
        coverHelpText,
    } = label;

    const { tableTitle, doText, doSmallText, dtpdText, dtpdSmallText } = table;

    const { calculatorData } = dataUrl;

    const { data, isLoading, isError, error } = useCalculatorQuery<FixedDeathCoverData[]>(calculatorData, 'fixed-death-cover-query', {
        queryKey: ['fixed-death-cover-calculator'],
        keepPreviousData: true,
        staleTime: 1000 * 60 * 5,
    });

    const { setData, deathOnlyFee, deathTPDFee } = useFixedDeathCalc();

    // const deathOnlyFeeDisplayText = deathOnlyFee ? currencyFormat.format(deathOnlyFee) : 'N/A';
    // const deathTPDFeeDisplayText = deathTPDFee ? currencyFormat.format(deathTPDFee) : 'N/A';

    if (isLoading) {
        return (
            <ListingLoader loadingMessage={`Loading data`} />
        );
    }

    if (isError || !data) {
        console.error(error);
        return (
            <ListingError errorMessage={`Error to initialize component`} />
        );
    }

    const ageOptions = data.map(item => {
        return {
            label: item.age.toString(),
            value: item.age.toString(),
        };
    });

    return (
        <div className="calculator-app">
            <div className="container">
                <div className="section-header">
                    <div className="section-heading">
                        <h2>{titleText}</h2>
                    </div>
                    <div className="section-description">
                        {HTMLReactParser(subtitleText)}
                    </div>
                </div>
                <div className="calculator-app-wrapper">
                    <div className="calc-wrapper">
                        <div className="row-calc">
                            <div className="col-calc">
                                <FixedDeathCalcForm
                                    ageOptions={ageOptions}
                                    labels={{
                                        ageLabel: labelAge,
                                        coverLabel: labelCover,
                                        genderLabel: labelGender,
                                        coverHelpText,
                                        submitLabel: calculateText,
                                    }}
                                    onSubmit={(selectedData): void => {
                                        const rowData = data.find(item => {
                                            return item.age === selectedData.age;
                                        });

                                        if (rowData) {
                                            setData({
                                                ...rowData,
                                                gender: selectedData.gender,
                                                cover: selectedData.cover,
                                            });
                                        }
                                    }}
                                />
                            </div>
                            <div className="col-calc">
                                {imageDesktopUrl && (
                                    <div className="image-calc-wrapper">
                                        <Image
                                            imgSrc={imageDesktopUrl}
                                            imgCustomClass="image-calc"
                                            imgAlt="fixed-death-calc-image"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {deathOnlyFee || deathTPDFee ? (
                        <>
                            <div className="calculator-result-wrapper">
                                <div className="calc-text-content">
                                    <h3>{tableTitle}</h3>
                                </div>
                                <div className="calculator-result-grid">
                                    <div className="calculator-result-col">
                                        <SimpleStatisticCard
                                            cardTitle={displayFormat(deathOnlyFee)}
                                            cardSubtitle={doText}
                                            cardDescription={doSmallText}
                                            className="calculator-result-card"
                                        />
                                    </div>
                                    <div className="calculator-result-col">
                                        <SimpleStatisticCard
                                            cardTitle={displayFormat(deathTPDFee)}
                                            cardSubtitle={dtpdText}
                                            cardDescription={dtpdSmallText}
                                            className="calculator-result-card"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="calc-text-content">
                                {HTMLReactParser(infoText)}
                            </div>
                        </>
                    ) : ''}
                </div>
            </div>
        </div>
    );
};

const queryClient = new QueryClient();

interface DeathCoverCalcInitProps {
    el: HTMLDivElement;
    props: FixedDeathCalcProps;
}

const FixedDeathCalcInit = ({ el, props }: DeathCoverCalcInitProps): void => {
    const root = ReactDOMClient.createRoot(el);

    root.render(
        <QueryClientProvider client={queryClient}>
            <FixedDeathCalc {...props} />
        </QueryClientProvider>
    );
};

export default FixedDeathCalcInit;
